
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchLeaveTypes, deleteLeaveType } from "../../redux/actions/leaveTypeActions";
import {
  FiRefreshCw,
  FiPlus,
  FiFilter,
  FiSearch,
  FiX,
  FiEdit3,
  FiToggleLeft,
  FiToggleRight,
  FiInfo,
  FiTrash2
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const LeaveTypes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { leaveTypes, loading, error } = useSelector((state) => state.leaveType || {});

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);

  useEffect(() => {
    dispatch(fetchLeaveTypes());
  }, [dispatch]);

  // Filter leave types
  const filteredLeaveTypes = leaveTypes.filter((leave) => {
    const matchesSearch =
      leave.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? leave.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
        label: "Active",
        icon: FiToggleRight,
      },
      inactive: {
        color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
        label: "Inactive",
        icon: FiToggleLeft,
      },
    };
    const config = statusConfig[status] || {
      color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
      label: status,
      icon: FiInfo,
    };
    const IconComponent = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}
      >
        <IconComponent className="text-xs" />
        {config.label}
      </span>
    );
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
  };

  // Open delete modal
  const openDeleteModal = (leave) => {
    setSelectedLeave(leave);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = () => {
    if (selectedLeave) {
      dispatch(deleteLeaveType(selectedLeave.id));
      setShowDeleteModal(false);
      setSelectedLeave(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedLeave(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Leave Types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Leave Types</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage different types of leave available for employees</p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <button
            onClick={() => dispatch(fetchLeaveTypes())}
            className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <FiRefreshCw className="text-lg" />
            Refresh
          </button>
          <button
            onClick={() => navigate("/dashboard/create-leave-type")}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <FiPlus className="text-lg" />
            Add Leave Type
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search leave types by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-xl transition-colors"
          >
            <FiFilter className="text-lg" />
            Filters
            {statusFilter && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">1</span>
            )}
          </button>
          {(searchTerm || statusFilter) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 px-4 py-3 rounded-xl transition-colors"
            >
              <FiX className="text-lg" />
              Clear
            </button>
          )}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
              <FiInfo className="text-white text-sm" />
            </div>
            <div>
              <p className="text-red-800 dark:text-red-300 font-medium">Error loading leave types</p>
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Leave Types Grid */}
      {filteredLeaveTypes.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <FiInfo className="text-4xl text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Leave Types Found</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {searchTerm || statusFilter ? "Try adjusting your search or filters" : "Get started by creating your first leave type"}
          </p>
          {(searchTerm || statusFilter) ? (
            <button onClick={clearFilters} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors">Clear Filters</button>
          ) : (
            <button onClick={() => navigate("/dashboard/create-leave-type")} className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors">Create Leave Type</button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeaveTypes.map((leave, index) => (
            <div key={leave.id || index} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{leave.name}</h3>
                  {getStatusBadge(leave.status)}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/dashboard/edit-leave-type/${leave.id}`)}
                    className="flex items-center gap-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 p-2 rounded-lg transition-colors"
                    title="Edit Leave Type"
                  >
                    <FiEdit3 className="text-sm" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(leave)}
                    className="flex items-center gap-1 text-red-500 hover:text-red-600 dark:hover:text-red-400 p-2 rounded-lg transition-colors"
                    title="Delete Leave Type"
                  >
                    <FiTrash2 className="text-sm" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Description</p>
                  <p className="text-gray-900 dark:text-white text-sm leading-relaxed">{leave.description || "No description provided"}</p>
                </div>
                {leave.total_leaves && (
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Leaves</p>
                    <p className="text-gray-900 dark:text-white font-medium">{leave.total_leaves} days</p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>ID: {leave.id}</span>
                  {leave.created_at && <span>Created: {new Date(leave.created_at).toLocaleDateString()}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedLeave && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Leave Type</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{selectedLeave.name}</strong>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypes;
